// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useMemo, useState } from 'react'

import { useKnowledgeBaseStore } from '../../../../shared/core/KnowledgeBaseProvider'
import type { FormatType } from './FormatSpecEditor'
import type { ReportSectionFormData } from './ReportSectionForm'
import type { ReportSectionItem } from './ReportSectionList'

interface ReportSectionMetadata {
  formatType: FormatType
  formatSpec: string[]
  formatGuidance: string
  sectionLabel: string
}

/**
 * Hook to manage Report KB sections.
 * Maps KB items to/from ReportSectionItem format.
 */
export function useReportSections() {
  const store = useKnowledgeBaseStore()
  const [editingItem, setEditingItem] = useState<ReportSectionItem | null>(null)

  // Map KB items to ReportSectionItem format
  const sections = useMemo<ReportSectionItem[]>(() => {
    return [...store.data.items].reverse().map(item => {
      const metadata = (item.metadata ?? {}) as Partial<ReportSectionMetadata>
      return {
        id: item.id,
        title: metadata.sectionLabel ?? item.content.slice(0, 50),
        content: item.content,
        formatType: metadata.formatType ?? 'prose',
        formatSpec: metadata.formatSpec ?? [],
        formatGuidance: metadata.formatGuidance ?? '',
      }
    })
  }, [store.data.items])

  // Add new section
  const addSection = useCallback(async (data: ReportSectionFormData) => {
    const metadata: Record<string, unknown> = {
      formatType: data.formatType,
      formatSpec: data.formatSpec,
      formatGuidance: data.formatGuidance,
      sectionLabel: data.title,
    }

    await store.addItem({
      content: data.content,
      score: null,
      metadata,
    })
  }, [store])

  // Update existing section
  const updateSection = useCallback(async (itemId: string, data: ReportSectionFormData) => {
    const item = store.data.items.find(i => i.id === itemId)
    if (!item) return

    const metadata: Record<string, unknown> = {
      formatType: data.formatType,
      formatSpec: data.formatSpec,
      formatGuidance: data.formatGuidance,
      sectionLabel: data.title,
    }

    await store.updateItem({
      itemId,
      content: data.content,
      score: null,
      metadata,
    })
  }, [store])

  // Delete section
  const deleteSection = useCallback(async (item: ReportSectionItem) => {
    await store.deleteItem(item.id)
  }, [store])

  // Edit mode handlers
  const startEditing = useCallback((item: ReportSectionItem) => {
    setEditingItem(item)
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingItem(null)
  }, [])

  const submitEdit = useCallback(async (data: ReportSectionFormData) => {
    if (editingItem) {
      await updateSection(editingItem.id, data)
      setEditingItem(null)
    } else {
      await addSection(data)
    }
  }, [editingItem, updateSection, addSection])

  return {
    sections,
    editingItem,
    loading: store.loading,
    saving: store.saving,
    error: store.error,
    addSection,
    updateSection,
    deleteSection,
    startEditing,
    cancelEditing,
    submitEdit,
    setError: store.setError,
    reload: store.reload,
  }
}
